import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@repo/ping-react-js";
import { Plus, Pencil, Info, Copy, Check } from "lucide-react";
import {
  fetchApiKeys,
  createApiKey,
  updateApiKey,
  deleteApiKey,
  type ApiKey,
} from "../api/apiKeys";
import "./ApiKeysPage.scss";

const EXPIRATION_OPTIONS = [
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 },
  { label: "180 days", value: 180 },
  { label: "365 days", value: 365 },
  { label: "No expiration", value: 0 },
];

const formatDate = (iso: string | null) => {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString();
};

const isExpired = (expiresAt: string | null) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

type ModalState =
  | { type: "none" }
  | { type: "create" }
  | { type: "token"; token: string; name: string }
  | { type: "rename"; key: ApiKey }
  | { type: "details"; key: ApiKey };

const ApiKeysPage = () => {
  const { accessToken } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: "none" });

  const loadKeys = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchApiKeys(accessToken);
      setKeys(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async (name: string, expiresIn: number) => {
    if (!accessToken) return;
    try {
      const res = await createApiKey(accessToken, {
        name,
        ...(expiresIn > 0 ? { expires_in: expiresIn } : {}),
      });
      setModal({ type: "token", token: res.api_key_token, name: res.name });
      loadKeys();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unable to create API key.");
      setModal({ type: "none" });
    }
  };

  const handleRename = async (apiKeyId: string, name: string) => {
    if (!accessToken) return;
    await updateApiKey(accessToken, apiKeyId, { name });
    setModal({ type: "none" });
    loadKeys();
  };

  const handleDelete = async (apiKeyId: string) => {
    if (!accessToken) return;
    await deleteApiKey(accessToken, apiKeyId);
    setModal({ type: "none" });
    loadKeys();
  };

  return (
    <>
      <header className="app-header">
        <div className="apikeys-header">
          <h1>API Keys</h1>
          <button
            className="apikeys-create-btn"
            onClick={() => setModal({ type: "create" })}
          >
            <Plus size={16} />
            Create API Key
          </button>
        </div>
      </header>
      <div className="app-content">
        {loading && <p className="apikeys-status">Loading...</p>}
        {error && <p className="apikeys-status apikeys-error">{error}</p>}
        {!loading && !error && keys.length === 0 && (
          <p className="apikeys-status">
            No API keys yet. Create one to get started.
          </p>
        )}
        {!loading && !error && keys.length > 0 && (
          <div className="apikeys-table-wrap">
            <table className="apikeys-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.api_key_id}>
                    <td>{k.name}</td>
                    <td>{formatDate(k.created_at)}</td>
                    <td>{formatDate(k.expires_at)}</td>
                    <td>
                      {isExpired(k.expires_at) ? (
                        <span className="apikeys-badge expired">Expired</span>
                      ) : (
                        <span className="apikeys-badge active">Active</span>
                      )}
                    </td>
                    <td>
                      <div className="apikeys-actions">
                        <button
                          className="apikeys-action-btn"
                          title="Rename"
                          onClick={() => setModal({ type: "rename", key: k })}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="apikeys-action-btn"
                          title="Details"
                          onClick={() => setModal({ type: "details", key: k })}
                        >
                          <Info size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal.type === "create" && (
        <CreateModal
          onClose={() => setModal({ type: "none" })}
          onCreate={handleCreate}
        />
      )}
      {modal.type === "token" && (
        <TokenModal
          token={modal.token}
          name={modal.name}
          onClose={() => setModal({ type: "none" })}
        />
      )}
      {modal.type === "rename" && (
        <RenameModal
          apiKey={modal.key}
          onClose={() => setModal({ type: "none" })}
          onRename={handleRename}
        />
      )}
      {modal.type === "details" && (
        <DetailsModal
          apiKey={modal.key}
          onClose={() => setModal({ type: "none" })}
          onDelete={handleDelete}
        />
      )}
    </>
  );
};

const CreateModal = ({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string, expiresIn: number) => Promise<void>;
}) => {
  const [name, setName] = useState("");
  const [expiresIn, setExpiresIn] = useState(90);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(name.trim(), expiresIn);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Create API Key</h2>
        <div className="modal-field">
          <label className="modal-label">Name</label>
          <input
            className="modal-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My API Key"
            autoFocus
          />
        </div>
        <div className="modal-field">
          <label className="modal-label">Expiration</label>
          <select
            className="modal-select"
            value={expiresIn}
            onChange={(e) => setExpiresIn(Number(e.target.value))}
          >
            {EXPIRATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="modal-actions">
          <button className="modal-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modal-btn primary"
            onClick={handleSubmit}
            disabled={!name.trim() || submitting}
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TokenModal = ({
  token,
  name,
  onClose,
}: {
  token: string;
  name: string;
  onClose: () => void;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">API Key Created</h2>
        <p className="modal-label">
          Your API key <strong>{name}</strong> has been created.
        </p>
        <div className="token-display">
          <span className="token-value">{token}</span>
          <button className="token-copy-btn" onClick={handleCopy} title="Copy">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
        <p className="token-warning">
          Copy this key now. You won't be able to see it again.
        </p>
        <div className="modal-actions">
          <button className="modal-btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const RenameModal = ({
  apiKey,
  onClose,
  onRename,
}: {
  apiKey: ApiKey;
  onClose: () => void;
  onRename: (id: string, name: string) => Promise<void>;
}) => {
  const [name, setName] = useState(apiKey.name);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onRename(apiKey.api_key_id, name.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Rename API Key</h2>
        <div className="modal-field">
          <label className="modal-label">Name</label>
          <input
            className="modal-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <button className="modal-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modal-btn primary"
            onClick={handleSubmit}
            disabled={!name.trim() || submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailsModal = ({
  apiKey,
  onClose,
  onDelete,
}: {
  apiKey: ApiKey;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await onDelete(apiKey.api_key_id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">API Key Details</h2>
        <div className="detail-row">
          <span className="detail-label">Name</span>
          <span className="detail-value">{apiKey.name}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Created</span>
          <span className="detail-value">{formatDate(apiKey.created_at)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Expires</span>
          <span className="detail-value">{formatDate(apiKey.expires_at)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Status</span>
          <span className="detail-value">
            {isExpired(apiKey.expires_at) ? "Expired" : "Active"}
          </span>
        </div>
        <div className="detail-actions">
          <button
            className={`modal-btn danger`}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting
              ? "Deleting..."
              : confirmDelete
                ? "Confirm Delete"
                : "Delete Key"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysPage;
